/**
 * The key component for our chat - renders the chat window.
 * Handles how it behaves - there are 4 (upd: 5) different forms.
 * Probably handles too much stuff.
 */
import React from "react";
import { connect } from "react-redux";
import styled, { keyframes, ThemeProvider } from "styled-components";
// A hack to fix the shadow dom styles.
import "react-scrollbar/dist/css/scrollArea.css";

import { device, financialChatActivated, isNarrowScreen, isVeryNarrowScreen, savedStateKey } from "../lib/constants";

import {
  getActiveModule,
  getChatMode,
  getChatPosition,
  getDisableMobile,
  getDisableSmallerDevice,
  getIconLogo,
  getIsChatOpen,
  getIsTeaserVisible,
  getLatestState,
  getMessagesHistory,
  getShouldShowConfirmSelection,
  getShouldShowContactInformation,
  getShouldShowDatePicker,
  getShouldShowTextInput,
  getShowAllAnswerOptions,
  getShowTeaserMaxTimes,
  getShowTitleFlash,
  getSlackAnimationStatus,
  getTeaserPause,
  getTeaserScrollPause,
  getTeaserShownTimes,
  wasTeaserClosed,
} from "../store";

import {
  addMobileOrLandscapeClass,
  getNarrowMidifier,
  getScreenLayout,
  urlConfig,
  isMobileUserAgent,
  Show,
} from "../lib/utils";

import {
  deepChange,
  getTheme,
  goToModule,
  initiateTitleFlashing,
  ModuleExtensionMixin,
  scrollBottom,
  stopTitleFlashing,
  updateTeaserShownTimes,
} from "../actions";

import { MessageWrapper, ActionsContainer } from "./messages/BotMessage";
import { Answer } from "./messages/Answer";
import { LoadMore } from "./HistoryHandler";
import FullscreenGallery from "./Gallery/FullscreenGalleryWrapper";
import PopoverWrapper from "./Gallery/Popover";
import { fadeInAnimation } from "../lib/animations";
import { ChatModes, MessageTypes, ModuleTypes, SlackAnimationStatus } from "../lib/types";
import { UserEventsAPI } from "../lib/userEventsAPI";

import { getIsIAactive } from "./InstantAnswers/selectors";
import { addMessage } from "./messages/actions";
import { getImages, shouldShowGallery } from "./Gallery/selectors";

import ChatHeader, { HeaderLogo, HeaderDiv } from "./Chat/ChatHeader";
import ChatContent from "./Chat/ChatContent";
import { Guide } from "./Guide/Guide";
import { getIsGuideActive } from "./Guide/selectors";

/**
 * A Chat component.
 */
class ChatComponent extends React.Component {
  /**
   * Constructor.
   */
  constructor(props) {
    super(props);

    const { showTeaserMaxTimes, teaserShownTimes, shouldRestoreSession, dispatch, teaserClosed } = props;

    /**
     * If we just restored the chat state after user has navigated to another page - no need to do the
     * opening animation.
     */
    if (shouldRestoreSession && !isNarrowScreen) {
      const savedState = JSON.parse(localStorage.getItem(savedStateKey) || {});
      savedState.activeModule = {
        ...savedState.activeModule,
        ...ModuleExtensionMixin,
        dispatch,
      };
      dispatch(deepChange(savedState));
    } else if (teaserShownTimes < showTeaserMaxTimes) {
      this.initTeaserScrollTimeout();
    } else if (teaserShownTimes >= showTeaserMaxTimes && teaserClosed !== ChatModes.circle) {
      this.initTeaserScrollTimeout(ChatModes.long);
    }
    window._famchat = this;
  }

  /**
   * TODO: add a human-readable overview of what is happening here.
   */
  componentDidUpdate(prevProps) {
    const {
      isChatOpen,
      messagesHistory,
      showTextInput,
      showDatePicker,
      showConfirmSelectionButton,
      activeModule,
      dispatch,
      isIaActive,
    } = this.props;

    // If there are no messages shown currently - show the first one.
    if ((isChatOpen && !prevProps.isChatOpen) || (financialChatActivated && messagesHistory.length === 0)) {
      this.startDialogue();
    }
    if (messagesHistory.length !== prevProps.messagesHistory.length) {
      this.initSlackConversationAnimation(prevProps);
      this.scrollToBottom();
    } else if (!prevProps.isChatOpen && isChatOpen) {
      this.scrollToBottom();
    } else if (
      prevProps.showTextInput !== showTextInput ||
      prevProps.showDatePicker !== showDatePicker ||
      prevProps.showConfirmSelectionButton !== showConfirmSelectionButton
    ) {
      this.scrollToBottom();
    }
    if (
      activeModule &&
      prevProps.activeModule &&
      prevProps.activeModule.type === ModuleTypes.slack &&
      prevProps.activeModule.type !== activeModule.type
    ) {
      this.clearTimeouts();
      dispatch(
        deepChange({
          "slackAnimation.typingAnimation": SlackAnimationStatus.off,
        })
      );
    }
    // Focus instant answers field after render.
    // Can be done in DumbTextInput.componentDidMount instead if turn it into a class-based component.
    if (isIaActive && !prevProps.isIaActive && !isNarrowScreen) {
      setTimeout(() => {
        window.famIAinput && window.famIAinput.focus();
      }, 20);
    }
  }

  /**
   * Handle typing slack animation.
   */
  initSlackConversationAnimation = () => {
    const { slackAnimation, activeModule, messagesHistory, dispatch, slackController } = this.props;

    let newSlackAnimation;

    if (
      activeModule &&
      activeModule.type === ModuleTypes.slack &&
      slackAnimation.typingAnimation !== SlackAnimationStatus.off
    ) {
      const lastMsg = messagesHistory[messagesHistory.length - 1];
      if (lastMsg.type === MessageTypes.USER_MESSAGE) {
        this.clearTimeouts();
        newSlackAnimation = {
          typingAnimation: SlackAnimationStatus.hide,
          animationTimeout: slackController.initDotsTimeout(),
          actionTimeout: slackController.initActionTimeout(),
        };
        dispatch(deepChange({ slackAnimation: newSlackAnimation }));
      } else if (lastMsg.type === MessageTypes.EXTRA_BOT_MESSAGE) {
        this.clearTimeouts();
        newSlackAnimation = {
          typingAnimation: SlackAnimationStatus.off,
          animationTimeout: null,
          actionTimeout: null,
        };
        dispatch(deepChange({ slackAnimation: newSlackAnimation }));
      }
    }
  };

  /**
   * Clear timeout objects for slackAnimation.
   */
  clearTimeouts = () => {
    const { slackAnimation } = this.props;
    slackAnimation.animationTimeout && clearTimeout(slackAnimation.animationTimeout);
    slackAnimation.actionTimeout && clearTimeout(slackAnimation.actionTimeout);
  };

  /**
   * Before the opening animation starts - we might first have to wait for the configured time
   * or wait until users scrolls the configured amount of page.
   * @property maxTeaserState - unroll the teaser at this state max.
   */
  initTeaserScrollTimeout = (maxTeaserState = ChatModes.teaser) => {
    /**
     * If there is a scroll delay, lets respect that first.
     * If there is no scroll delay, then lets go right to the bot timeout.
     * */
    const { teaserPauseScroll, shouldRestoreSession, disableSmallerDevices, disableMobile } = this.props;

    /* Do not show the teaser on smaller mobile devices, when it is disabled in the config */
    /* Do not show the teaser when it is should restore and on mobile */
    /* This logic could  be done more cleanly, but I wanted to make sure this was readable and correct */
    /* When mobile is disabled, show the long chat bar, not the button */
    let blockTeaser = false;
    if (disableSmallerDevices && isVeryNarrowScreen) {
      blockTeaser = true;
    }

    if (shouldRestoreSession && isNarrowScreen) {
      blockTeaser = true;
    }

    if (disableMobile && isNarrowScreen) {
      this.initChatAnimation(maxTeaserState);
      return false;
    }

    if (teaserPauseScroll === "0px") {
      if (!blockTeaser) {
        this.initChatAnimation(maxTeaserState);
      }
    } else {
      /**
       * If the teaser is configured to start only after user scrolls the page.
       */
      let lastScrollPosition = 0;
      let ticking = false;
      let teaserExecuted = false;
      window.addEventListener("scroll", () => {
        lastScrollPosition = window.scrollY;
        if (!ticking) {
          window.requestAnimationFrame(() => {
            if (lastScrollPosition > parseInt(teaserPauseScroll.replace("px", "")) && !teaserExecuted) {
              this.initChatAnimation(maxTeaserState);
              teaserExecuted = true;
            }
            ticking = false;
          });
          ticking = true;
        }
      });
    }
  };

  /**
   * Initiate the chat animation from the circle to the long state.
   * And then from the long to the teaser and start the dialogue.
   * @property maxTeaserState - unroll the teaser max to this state.
   */
  initChatAnimation = (maxTeaserState) => {
    const { teaserPause, teaserClosed } = this.props;

    // Don't show up automatically in financial mode.
    if (financialChatActivated) {
      return;
    }

    // Transform from the circle state to the long.
    if (teaserClosed !== ChatModes.circle) {
      setTimeout(
        () => {
          this.changeForm(ChatModes.circle, ChatModes.long);
        },
        urlConfig.noAnimation ? 10 : teaserPause
      );
    }

    // Then - transform from long state to open.
    if (maxTeaserState !== ChatModes.long) {
      if (![ChatModes.circle, ChatModes.long].includes(teaserClosed)) {
        setTimeout(
          () => {
            urlConfig.noAnimation ? this.openChat() : this.unrollTeaser();
          },
          urlConfig.noAnimation ? 10 : teaserPause + 1000
        );
      }
    }
  };

  /**
   * Change the chat form from one state to another.
   * If the current chat state not equals expected - do nothing.
   * @param from chatModes
   * @param to chatModes
   */
  changeForm = (from, to) => {
    const { chatMode, dispatch } = this.props;
    if (chatMode !== from) {
      return;
    }
    dispatch(deepChange({ chatMode: to }));
  };

  /**
   * Change chat form the the long to the teaser and start the dialogue.
   */
  unrollTeaser = () => {
    this.changeForm(ChatModes.long, urlConfig.forceOpen ? ChatModes.open : ChatModes.teaser);
    updateTeaserShownTimes();
    this.startDialogue();
  };

  /**
   * Main entrypoint for start of teaser animation/opening and messages being dispatched.
   * TODO: move this from class methods to independent actions.
   */
  startDialogue = () => {
    const { dispatch, messagesHistory, isChatOpen, showTitleFlash } = this.props;
    if (messagesHistory.length === 0) {
      dispatch(
        addMessage({
          type: MessageTypes.NEW_SESSION,
          date: new Date().getTime(),
        })
      );
      const startModule = (urlConfig.openFamilyAssetsBot && parseInt(urlConfig.openFamilyAssetsBot)) || 1;
      dispatch(goToModule(startModule));

      if (!isChatOpen && showTitleFlash) {
        dispatch(initiateTitleFlashing());
      }
    }
  };

  /**
   * Scroll to bottom of the scroll area.
   */
  scrollToBottom = (timeout = 10) => {
    scrollBottom(timeout);
  };

  /**
   * Toggle chat visibility.
   * That's an old comment. Now it's more complicated.
   */
  openChat = () => {
    const { dispatch, chatMode } = this.props;
    dispatch(stopTitleFlashing());

    const state = dispatch(getLatestState); // TODO: anyway this will be a functional component soon.

    if (chatMode !== ChatModes.open) {
      dispatch(UserEventsAPI.sendChatOpen());
      dispatch(deepChange({ chatMode: ChatModes.open, showAllAnswerOptions: true }));
      scrollBottom(400);
    }

    if (financialChatActivated && !getShowAllAnswerOptions(state)) {
      dispatch(deepChange({ showAllAnswerOptions: true }));
      scrollBottom(400);
    }
  };

  /**
   * Component for creating right area in full screen version of the chat - when window.financialChatActivated === true.
   * Also renders the whole chat content in the regular mode.
   * TODO: Probably can be simplified to move the div.chat into ChatContent component.
   */
  renderMainChatContent = () => {
    return (
      <>
        <ChatHeader />
        <div className="chat">
          <ChatContent />
        </div>
      </>
    );
  };

  /**
   * Render `ControlBar` component - thing that handles how the whole chat is rendered in general -
   * either it is:
   * 1. just a circled icon
   * 2. a long bar with the icon and the Adviser name
   * 3. a small teaser with few welcoming messages
   * 4. a fully open chat ready for interactions
   * 5. or takes a whole screen in the "Financial Chat" mode, covering the left part with the background picture.
   */
  renderControlBar = () => {
    const { iconLogo, chatPosition, chatMode, showContactInformationMessage, isIaActive, disableMobile } = this.props;

    let className = chatMode;

    if (isIaActive) {
      className += ` ia ${getScreenLayout()} ${getNarrowMidifier()}`;
    }

    if (disableMobile && isMobileUserAgent) {
      className += " disable-mobile";
    }

    // TODO TestRequired: show only maxed out chat in financial mode.
    if (financialChatActivated && chatMode !== ChatModes.open) {
      return null;
    }

    return (
      <ControlBar
        className={className}
        iconLogo={iconLogo}
        onClick={() => this.openChat()}
        chatPosition={chatPosition}
        showContactInformationMessage={showContactInformationMessage}
      >
        {/* A vertical bar for debug only - helps to center the elements. */}
        {/* <div className="middle-bar"/> */}
        {financialChatActivated ? this.renderFinancialFullscreen() : this.renderMainChatContent()}
      </ControlBar>
    );
  };

  /**
   * Render chat in full screen mode.
   * When window.financialChatActivated === true.
   */
  renderFinancialFullscreen = () => {
    const { iconLogo, images, chatMode } = this.props;

    if (chatMode !== ChatModes.open) {
      return null;
    }

    const financialBackground = images && images[0];

    return (
      <PopoverWrapper financialBackground={financialBackground}>
        <div className={addMobileOrLandscapeClass("gallery column financial")}>
          <div className="colored-overflow">
            <img className="financial-logo" src={iconLogo} alt="" />
            <span>Financial Assistant</span>
          </div>
        </div>
        <div className={addMobileOrLandscapeClass("right-column column financial")}>{this.renderMainChatContent()}</div>
      </PopoverWrapper>
    );
  };

  /**
   * The main Render method.
   */
  render() {
    const { showGallery, theme, showGuide } = this.props;

    let mode = "chat";
    if (showGallery) {
      mode = "gallery";
    } else if (showGuide) {
      mode = "guide";
    }

    return (
      <>
        <ThemeProvider theme={theme}>
          <Show when={mode === "gallery"}>
            <FullscreenGallery />
          </Show>

          <Show when={mode === "guide"}>
            <Guide />
          </Show>

          <Show when={mode === "chat"}>{this.renderControlBar()}</Show>
        </ThemeProvider>
      </>
    );
  }
}

/**
 * Animation for the header logo to pop in.
 */
const popInAnimation = keyframes`
  0% { opacity: 0; transform: scale(0.5); }
  100% { opacity: 1; transform: scale(1); }
`;

/**
 * Animation for the pulsating button on hover.
 */
const hoverPulseButtonAnimation = keyframes`
  0%,to{
    transform:scale(1);
    box-shadow:0 0 20px 0 hsla(0,0%,7%,.2)
    }
  50%{
    transform:scale(1.05);
    box-shadow:0 0 20px 0 hsla(0,0%,7%,.6)
  }
`;

/**
 * Animation for the pulsating button 1 second after 9 of pause.
 */
const infinitePulsingButtonAnimation = keyframes`
  0%,10%{
    transform:scale(1);
    box-shadow:0 0 20px 0 hsla(0,0%,7%,.2)
    }
  5%{
    transform:scale(1.05);
    box-shadow:0 0 20px 0 hsla(0,0%,7%,.6)
  }
`;

/**
 * A colored chat top bar.
 */
const ControlBar = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  box-sizing: border-box;
  background-color: white;
  font-family: ${(props) => props.theme.fontFamily || "arial"};

  font-weight: 500;
  font-size: ${(props) => props.theme.fontSize || "13px"};
  color: #282932;

  transition: all ${(props) => (props.theme.noAnimation || financialChatActivated ? "0s" : "0.4s")};
  animation: ${popInAnimation} ${(props) => (props.theme.noAnimation || financialChatActivated ? "0s" : "0.3s")};

  // Disable input field shadow for iOS.
  input[type=text] {
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
  }

  // Disable shitty clear button for IE.
  input::-ms-clear {
      display: none;
  }

  /**
  For debug only.
   */
  .middle-bar {
    width: 1px;
    background-color: red;
    height: 100%;
    position: absolute;
    top: 0;
    left: 50%;
  }

  * {
    font-family: ${(props) => props.theme.fontFamily || "arial"};
    box-sizing: border-box;
  }

  a, a:link, a:visited, a:focus, a:hover, a:active {
    text-decoration: none;
    outline-offset: unset;
    outline: none;
  }

  button:focus {
    outline: none;
  }

  .contactInformation {
    padding: 0 15px 10px;
    color: #aaa;
  }

  .controls {
    position: absolute;
    right: 32px;
    top: 32px;

    &.left {
      width: 70px;
      opacity: 0;
      left: 32px;
      top: 32px;
      transition: opacity 0.3s ease-out;
    }
  }

  .title {
    position: absolute;
    left: 89px;
    opacity: 0;
    animation: ease 0.2s 0.5s forwards ${fadeInAnimation};
    transition: color 0.3s ease-out, left 0.3s ease-out;

    .link {
      max-width: 200px;
      line-height: 16px;
      display: block;
      color: ${(props) => props.theme.brandColor};
      transition: color 0.3s ease-out;
    }
  }

  /**
   * To make the close icon easier to click.
   */
  .close-area {
    width: 30px;
    height: 30px;
    display: flex;
    align-items: center;
    justify-content: center;

    svg {
      &:hover {
        path {
          fill: #595959;
        }
      }
    }
  }

  .back-area {
    cursor: pointer;
    height: 30px;
    display: flex;
    align-items: center;
    justify-content: center;

    span {
      padding-left: 10px;
      color: #bdbdbd;
      font-size: 17px;
    }

    svg {
      margin-bottom: 1px;
      path {
        stroke: #bdbdbd;
      }
    }

    &:hover {
      span {
        color: #595959;
      }
      svg path {
        stroke: #595959;
      }
    }
  }

  .chat {
    width: 100%;
    height: 100%;
    display: flex;
    min-height: 76px;
  }

  position: fixed;
  right: ${(props) => (props.chatPosition === "right" ? "20px" : "unset")};
  left: ${(props) => (props.chatPosition === "left" ? "20px" : "unset")};
  bottom: 20px;
  border-radius: 99px;

  &.circle,
  &.long {
    height: 70px;
  }

  &:not(.circle) {
    box-shadow: 0 4px 20px 0 hsla(0, 0%, 7%, 0.2);
  }

  &.circle {
    padding: 0;

    height: 55px;
    width: 55px;
    z-index: 1000;

    display: inline-block;
    vertical-align: middle;

    cursor: pointer;
    background: 50% no-repeat;

    box-shadow: 0 0 20px 0 hsla(0, 0%, 7%, 0.2);
    border-radius: 50%;
    background-size: 70%;

    .title,.controls,
    .chat {
      display: none;
    }

    ${HeaderDiv} {
      display: block;
      padding: 0;
    }

    // Need to add additional class for this to not interfere with pop in animation.
    // animation: ${infinitePulsingButtonAnimation} 10s infinite;

    // And to make this work also properly I also need to invent something.
    // &:hover {
    //   animation: ${hoverPulseButtonAnimation} 1s infinite;
    // }
  }

  &.long {
    width: 350px;
    height: 70px;
    border-radius: 99px;
    max-height: 100px; // For the roll up animation.

    .chat {
      display: none;
    }
    .controls {
      top: 21px;
    }
  }

  /**
    Teaser mode styles.
   */
  &.teaser {
    width: 350px;
    border-radius: 14px;
    max-height: ${(props) => (props.showContactInformationMessage ? "450px" : "400px")};

    @media ${device.mobile} {
      border-radius: 32px;

      &.disable-mobile {
        ${MessageWrapper} {
          padding-top: 0;

          ${ActionsContainer} {
            display: flex;
            justify-content: space-between;
            flex-wrap: wrap;
          }

          ${Answer} {
            max-width: calc(50% - 6px);
          }
        }

        ${HeaderDiv} {
          border-bottom: none;
          padding: 10px 20px;

          ${HeaderLogo} {
            height: 35px;
            width: 35px;

            &:after {
              right: 2px;
            }
          }

          .controls {
            top: 12px;
          }

          .title > .link {
            display: none;
          }
        }
      }
    }

    ${MessageWrapper} {
      padding-top: 18px;
    }

    ${LoadMore} {
      display: none;
    }
  }

  &.teaser, &.open {
    ${HeaderDiv} {
      padding: 20px 20px 10px 20px;
      border-bottom: 1px solid #EAEFF6;
    }
  }

  &.open {
    width: 350px;
    height: 620px;
    bottom: 20px;
    overflow: hidden;
    z-index: 1000;
    border-radius: 14px;
    max-height: 720px;

    &.ia.desktop:not(.narrow) {
      height: 95%;
      max-height: 95%;
    }

    @media ${device.mobile} {
      border-radius: 32px;
    }
    @media ${device.landscape} {
      height: 100%;
      border-radius: unset;
      max-height: 100%;
      width: 100%;
      right: 0;
      left: 0;
      bottom: 0;
    }
  }

  @media ${device.mobile} {
    &.long {
      width: calc(100% - 40px);
    }
    &.teaser,&.open {
      width: 100%;
      right: 0;
      left: 0;
      bottom: 0;
      border-bottom-left-radius: unset;
      border-bottom-right-radius: unset;

      .chat {
        //min-height: 300px;
      }
    }
    &.open {
      height: 100%;
      border-radius: unset;
      max-height: 100%;
    }
  }
`;

/**
 * Map state to props.
 */
function mapStateToProps(state) {
  return {
    iconLogo: getIconLogo(state),
    messagesHistory: getMessagesHistory(state),
    activeModule: getActiveModule(state),
    isChatOpen: getIsChatOpen(state),
    teaserPause: getTeaserPause(state),
    teaserPauseScroll: getTeaserScrollPause(state),
    isTeaserVisible: getIsTeaserVisible(state),
    teaserShownTimes: getTeaserShownTimes(),
    showTeaserMaxTimes: getShowTeaserMaxTimes(state),
    showTitleFlash: getShowTitleFlash(state),
    disableSmallerDevices: getDisableSmallerDevice(state),
    disableMobile: getDisableMobile(state),
    showTextInput: getShouldShowTextInput(state),
    showDatePicker: getShouldShowDatePicker(state),
    showConfirmSelectionButton: getShouldShowConfirmSelection(state),
    chatPosition: getChatPosition(state),
    teaserClosed: wasTeaserClosed(state),
    shouldRestoreSession: state.shouldRestoreSession,
    showContactInformationMessage: getShouldShowContactInformation(state),
    showGallery: shouldShowGallery(state),
    chatMode: getChatMode(state),
    theme: getTheme(state),
    images: getImages(state),
    slackAnimation: getSlackAnimationStatus(state),
    slackController: state.slackController,
    isIaActive: getIsIAactive(state),
    showGuide: getIsGuideActive(state),
  };
}

export default connect(mapStateToProps)(ChatComponent);
